import { useLatestCallback } from '@hover/blueprint';
import type { MouseEvent } from 'react';

import { useTrackingMetadata } from 'src/shared/hooks/useSegmentHistory';
import type { ButtonPressed } from 'src/shared/telemetry';
import { buttonPressed } from 'src/shared/telemetry';

import {
  useFavoriteCustomizedImage,
  useFavoriteDisplayImage,
} from '../components/Utils';
import type {
  AiOrSampleDisplayImage,
  AiOrSampleGeneratedImage,
} from '../types';
import { isDisplay, isGenerated, isSample } from '../types';

import { useHomeownerAppContext } from './useHomeownerAppContext';

export interface UseFavoriteHandlerProps {
  image?: AiOrSampleGeneratedImage | AiOrSampleDisplayImage;
  /**
   * This is a flag to denote a special case where we need to invoke a different
   * mutation. Specifically, when the image in question was generated as a
   * result of customizing an existing display image and we don't know whether
   * it has an associated display image yet.
   */
  isCustomizedImage?: boolean;
  /**
   * By default, the handler acts as a toggle on the favorite state. In certain
   * cases, a button does not flip the state but instead sets it to true or
   * false explicitly. When newFavoriteState is present it overrides the
   * default toggle behavior
   */
  newFavoriteState?: boolean;
  onFavorite?: (favorite: boolean) => void;
  onFavoriteSample?: () => void;
  optionalTrackingProperties?: Partial<ButtonPressed>;
}

const useFavoriteHandler = ({
  image,
  onFavorite,
  onFavoriteSample,
  optionalTrackingProperties,
  isCustomizedImage,
  newFavoriteState: newFavoriteStateProp,
}: UseFavoriteHandlerProps) => {
  const { setCurrentMenu, trackingData } = useHomeownerAppContext();

  const favoriteDisplayImage = useFavoriteDisplayImage();
  const favoriteCustomizedImage = useFavoriteCustomizedImage();

  const {
    metadata: { partner_id: partnerId },
  } = useTrackingMetadata();

  return useLatestCallback(
    async (event?: MouseEvent) => {
      event?.stopPropagation();

      if (image) {
        const isSampleImage = isSample(image);
        const isDisplayImage = isDisplay(image);
        const isGeneratedImage = isGenerated(image);

        const toggledFavoriteState = isDisplayImage
          ? !image.favoritedAt
          : !image.favorite;

        const newFavoriteState = newFavoriteStateProp ?? toggledFavoriteState;
        const favoriteStateWillChange =
          newFavoriteState === toggledFavoriteState;

        buttonPressed({
          ...trackingData,
          ...(isSampleImage
            ? {
                output_id: image.sampleId,
              }
            : {
                ai_display_image_id: isDisplayImage ? image.id : undefined,
                ai_original_image_id: image.aiOriginalImageId,
                ai_generated_image_id: isDisplayImage
                  ? image.aiGeneratedImage.id
                  : image.id,
              }),
          button_location: 'Save to module',
          button_text: newFavoriteState ? 'Favorited' : 'Unfavorited',
          partner_id: partnerId,
          primary_cta: false,
          track_id: newFavoriteState
            ? 'save-to-saved-ideas'
            : 'remove-from-saved-ideas',
        });

        onFavorite?.(newFavoriteState);

        if (isSampleImage) {
          onFavoriteSample?.();
          return null;
        }

        if (favoriteStateWillChange) {
          if (isGeneratedImage) {
            return favoriteCustomizedImage(image);
          }

          return favoriteDisplayImage(image);
        }
      }

      return null;
    },
    [
      favoriteCustomizedImage,
      favoriteDisplayImage,
      image,
      isCustomizedImage,
      newFavoriteStateProp,
      onFavorite,
      onFavoriteSample,
      optionalTrackingProperties,
      setCurrentMenu,
      trackingData,
    ],
  );
};

export { useFavoriteHandler };
